import React, { useState, useRef, useEffect, useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import useOptionList from '../../hooks/useOptionList';

// The "type to filter, '+' to add a new one, ⚙️ Manage List to prune" control
// that modules built by hand (event types, expense heads, designations, ...).
//
// Pass `storageKey` to have the option list kept on the server, shared by
// every user and editable under Settings › Dropdown Lists.
export interface SearchableSelectProps {
  label?: ReactNode;
  value?: string;
  onChange?: (value: string) => void;
  /** Controlled option list. Omit to let the component own it via storageKey. */
  options?: readonly string[];
  /**
   * Starting values for a list that is neither controlled nor registered in
   * utils/optionLists; a registered storageKey takes its defaults from there.
   */
  defaultOptions?: readonly string[];
  /** Parish-wide list key (see utils/optionLists) for the uncontrolled form. */
  storageKey?: string;
  onOptionsChange?: (next: string[]) => void;
  /** Show the '+' button that adds the typed value to the list. */
  allowAdd?: boolean;
  /** Show the Manage List footer. */
  allowManage?: boolean;
  placeholder?: string;
  required?: boolean;
  /**
   * Locks the field: no typing, no dropdown, and the add/manage affordances are
   * hidden. Used to stop a saved record being re-pointed at a different parent
   * while it is being edited.
   */
  disabled?: boolean;
  /** Validation message, rendered under the field the way Select does. */
  error?: ReactNode;
  fullWidth?: boolean;
  containerStyle?: CSSProperties;
  isDarkMode?: boolean;
  onNotify?: (message: string, type?: 'success' | 'error') => void;
}

const SearchableSelect = ({
  label,
  value = '',
  onChange,                 // (selectedValue) => void
  options: controlledOptions,
  defaultOptions = [],
  storageKey,               // e.g. 'church_event_types'
  onOptionsChange,
  allowAdd = true,
  allowManage = true,
  placeholder = 'Search or type new...',
  required = false,
  disabled = false,
  error,
  fullWidth = false,
  containerStyle,
  isDarkMode,
  onNotify                  // (message, type) => void
}: SearchableSelectProps) => {
  const s = useStyles(isDarkMode);

  // The shared list for `storageKey`. With no key the hook is inert (an empty,
  // unsaved list) and the component owns its options below.
  const persisted = useOptionList(
    storageKey || '',
    storageKey ? (defaultOptions.length ? defaultOptions : undefined) : []
  );

  const [internalOptions, setInternalOptions] = useState<string[]>(
    () => [...(controlledOptions || defaultOptions)]
  );

  const options = controlledOptions || (storageKey ? persisted.options : internalOptions);

  const setOptions = (next: string[]) => {
    if (storageKey) persisted.setOptions(next);
    else if (!controlledOptions) setInternalOptions(next);
    onOptionsChange?.(next);
  };

  const [text, setText] = useState<string>(value ?? '');
  const [open, setOpen] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setText(value ?? ''); }, [value]);

  // Close on outside click — more reliable than the setTimeout-on-blur trick.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(
    () => (options as string[]).filter((o) => String(o).toLowerCase().includes(text.toLowerCase())),
    [options, text]
  );

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) {
      onNotify?.('Please type a name first.', 'error');
      return;
    }
    // Case-insensitive: adding "Pastor" when "pastor" already exists must reuse
    // the existing entry, not create a near-duplicate.
    const existing = options.find((o) => o.trim().toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      onChange?.(existing);
      setOpen(false);
      return;
    }
    setOptions([...options, trimmed]);
    onNotify?.(`"${trimmed}" added to the list.`, 'success');
    onChange?.(trimmed);
    setOpen(false);
  };

  const pick = (option: string) => {
    setText(option);
    onChange?.(option);
    setOpen(false);
  };

  const toggleDeletion = (option: string) =>
    setSelectedForDeletion((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );

  const confirmDelete = () => {
    if (!selectedForDeletion.length) return;
    setOptions(options.filter((o) => !selectedForDeletion.includes(o)));
    setSelectedForDeletion([]);
    setShowManage(false);
  };

  return (
    <div ref={wrapperRef} style={{ ...s.inputGroup, position: 'relative', ...(fullWidth ? s.fullWidth : {}), ...containerStyle }}>
      {label && (
        <label style={s.label}>
          {label}
          {required && <span style={{ color: s.t.error, marginLeft: '4px' }}>*</span>}
        </label>
      )}

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          type="text"
          value={text}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => { setText(e.target.value); onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => { if (!disabled) setOpen(true); }}
          style={{
            ...s.input,
            ...(allowAdd && !disabled ? { paddingRight: '45px' } : {}),
            ...(error ? { borderColor: s.t.error } : {}),
            ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {})
          }}
        />
        {allowAdd && !disabled && (
          <button type="button" onClick={handleAdd} style={s.addBtn} title="Add to list">+</button>
        )}
      </div>

      {error && (
        <span style={{ fontSize: s.t.fontSize.sm, color: s.t.error, marginTop: '4px' }}>{error}</span>
      )}

      {open && !disabled && (
        <div style={s.dropdownMenu}>
          {filtered.length > 0 ? (
            filtered.map((option, idx) => (
              <div
                key={idx}
                style={s.dropdownItem}
                onMouseDown={() => pick(option)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = s.t.bgHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {option}
              </div>
            ))
          ) : (
            <div style={{ padding: '10px 14px', fontSize: s.t.fontSize.base, color: s.t.textFaint }}>
              {allowAdd ? "Click '+' to add" : 'No matches'}
            </div>
          )}
          {allowManage && (
            <div
              style={s.dropdownFooter}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); setShowManage(true); }}
            >
              ⚙️ Manage List...
            </div>
          )}
        </div>
      )}

      {showManage && (
        <div style={s.notificationOverlay} onClick={() => setShowManage(false)}>
          <div style={{ ...s.notificationBox, textAlign: 'left', minWidth: '340px' }} onClick={(e) => e.stopPropagation()}>
            <h4 style={s.notificationTitle}>Manage List</h4>
            <p style={{ ...s.notificationText, marginBottom: '12px', fontSize: s.t.fontSize.base }}>
              Select the entries you want to remove.
            </p>
            <div style={{ maxHeight: '260px', overflowY: 'auto', border: `1px solid ${s.t.border}`, borderRadius: s.t.radius.sm, marginBottom: '20px' }}>
              {options.map((option, idx) => (
                <label
                  key={idx}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 15px', cursor: 'pointer',
                    color: s.t.textMain, fontSize: s.t.fontSize.md,
                    borderBottom: `1px solid ${s.t.borderDashed}`
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedForDeletion.includes(option)}
                    onChange={() => toggleDeletion(option)}
                  />
                  {option}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" style={{ ...s.viewBtn, flex: 1, padding: '10px' }} onClick={() => { setSelectedForDeletion([]); setShowManage(false); }}>
                Cancel
              </button>
              <button
                type="button"
                style={{ ...s.dangerBtn, flex: 1, padding: '10px', opacity: selectedForDeletion.length ? 1 : 0.5 }}
                disabled={!selectedForDeletion.length}
                onClick={confirmDelete}
              >
                Remove ({selectedForDeletion.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
